/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import classnames from 'classnames';
import * as React from 'react';

import { Heading, IHeadingProps } from '@msdyn365-commerce-modules/utilities';
import { IImageData, IImageSettings, Image, RichText, RichTextComponent } from '@msdyn365-commerce/core';

import { IProductDataEntryData } from './product-data-entry.data';
import { IProductAttributesItemsData, IProductDataEntryProps, IProductTastingNotesItemsData } from './product-data-entry.props.autogenerated';

/**
 *
 * ProductDataEntry component
 * @extends {React.PureComponent<IProductDataEntryProps<IProductDataEntryData>>}
 */
class ProductDataEntry extends React.PureComponent<IProductDataEntryProps<IProductDataEntryData>> {

    //------------------------------------------------------
    //------------------------------------------------------
    public render(): JSX.Element {
        const config = this.props.config;
        const hasTastingNotes = config.productTastingNotesItems && !!config.productTastingNotesItems.length;
        const hasAttributes = config.productAttributesItems && !!config.productAttributesItems.length;

        return (
            <div className={classnames('product-data-entry', config.customClass)}>

                {config.productImage && this._renderHeroImage(config.productImage)}
                {config.productCaption &&
                    <div className='product-data-entry__caption'>
                        {this._renderRichText(config.productCaption, 'product-data-entry__caption-text')}
                    </div>
                }

                <div className='product-data-entry__text'>
                    {config.productHeading && this._renderHeading(config.productHeading, 'product-data-entry__heading')}
                    {config.productSubHeading && this._renderHeading(config.productSubHeading, 'product-data-entry__subheading')}
                    {config.productDescription && this._renderRichText(config.productDescription, 'product-data-entry__description')}

                    <div className='product-data-entry__tasting-notes'>
                        {config.productTastingNotesHeading && this._renderHeading(config.productTastingNotesHeading, 'product-data-entry__tasting-notes__heading')}
                        {config.productTastingNotesItems && hasTastingNotes && this._renderTastingNotesItems(config.productTastingNotesItems)}
                    </div>

                    <div className='product-data-entry__attributes'>
                        {config.productAttributesHeading && this._renderHeading(config.productAttributesHeading, 'product-data-entry__attributes__heading')}
                        {config.productAttributesItems && hasAttributes && this._renderProductAttributesItems(config.productAttributesItems)}
                    </div>
                </div>

            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _renderHeading(heading: IHeadingProps, className?: string): JSX.Element {
        return (
            <Heading
                className={classnames(className)}
                headingTag={heading.tag}
                text={heading.text}
            />
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _renderRichText(text: RichText, className?: string): JSX.Element {
        return (
            <RichTextComponent
                className={classnames(className)}
                text={text}
            />
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _renderHeroImage(productImage: IImageData): JSX.Element {
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=0&h=814&m=6`, w: 0, h: 0 },
                lg: { q: `w=0&h=814&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <div className='product-data-entry__image'>
                <Image
                    {...productImage}
                    gridSettings={this.props.context.request.gridSettings!}
                    imageSettings={productImage.imageSettings || defaultImageSettings}
                />
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _renderTastingNotesItems(productTastingNotesItems: IProductTastingNotesItemsData[]): JSX.Element {
        const defaultImageSettings: IImageSettings = {
            viewports: {
                xs: { q: `w=25&h=25&m=6`, w: 0, h: 0 },
                lg: { q: `w=25&h=25&m=6`, w: 0, h: 0 }
            },
            lazyload: true
        };

        return (
            <div className='product-data-entry__tasting-notes__items'>
                {productTastingNotesItems.map((item: IProductTastingNotesItemsData, idx: number) => (
                    <div key={idx} className={classnames('product-data-entry__tasting-notes__item', item.tastingNotesCustomClass)}>
                        {item.tastingNotesIcon &&
                            <div className='product-data-entry__tasting-notes__icon'>
                                <Image
                                    {...item.tastingNotesIcon}
                                    gridSettings={this.props.context.request.gridSettings!}
                                    imageSettings={item.tastingNotesIcon.imageSettings || defaultImageSettings}
                                />
                            </div>
                        }
                        <div className='product-data-entry__tasting-notes__name'>
                            {item.tastingNotesName}
                        </div>
                    </div>
                ))}
            </div>
        );
    }

    //------------------------------------------------------
    //------------------------------------------------------
    private _renderProductAttributesItems(productAttributesItems: IProductAttributesItemsData[]): JSX.Element {
        return (
            <div className='product-data-entry__attributes__items'>
                {productAttributesItems.map((item: IProductAttributesItemsData, idx: number) => (
                    <div key={idx} className={classnames('product-data-entry__attributes__item', item.attributeCustomClass)}>
                        {item.attributeName && this._renderHeading(item.attributeName, 'product-data-entry__attributes__name')}
                        {item.attributeText && this._renderRichText(item.attributeText, 'product-data-entry__attributes__text')}
                    </div>
                ))}
            </div>
        );
    }
}

export default ProductDataEntry;
